/** @jsxImportSource ./simboluri-jsx */
// «Planul de cofrare» — tabul vederii «Componente» care răspunde la întrebarea
// dulgherului: DIN CÂTE COFRAJE DIFERITE se toarnă structura, de câte ori se
// montează fiecare, și pe ce grilă de deschideri stă.
//
// Cinci familii (`cofraje.py`): stâlpi (secțiune × înălțime), grinzi
// (secțiunea inimii × LUMINA — lungimea cofrajului, nu a axei), grinzi de
// fundare (inima cofrată, pe ax), pereți de beton (grosime × înălțime) și
// panourile de planșeu (ochiul × grosimea plăcii). Ce nu se cofrează e spus
// pe față, nu presupus. Grila (`interax.py`) e cea pe care scorul o plătește
// la «grilă»: travee pe modulul de parcare și deschideri egale.

import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { bani } from "./bani";
import { numar, useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import type { FamilieCofraj, InteraxStalpi, PlanCofrare, TipCofraj }
  from "./tipuri";

const nr = (v: number | null | undefined, zec = 0) =>
  v == null ? "—" : numar(v, { maximumFractionDigits: zec });

const FAMILII: { cheie: FamilieCofraj; nume: Cheie; ce: Cheie }[] = [
  { cheie: "stalpi", nume: "struct.cof.fam_stalpi", ce: "struct.cof.fam_stalpi_ce" },
  { cheie: "grinzi", nume: "struct.cof.fam_grinzi", ce: "struct.cof.fam_grinzi_ce" },
  { cheie: "fundatie", nume: "struct.cof.fam_fundatie",
    ce: "struct.cof.fam_fundatie_ce" },
  { cheie: "pereti", nume: "struct.cof.fam_pereti", ce: "struct.cof.fam_pereti_ce" },
  { cheie: "placa", nume: "struct.cof.fam_placa", ce: "struct.cof.fam_placa_ce" },
];

function Antet({ nume, ce, n, tinta, m2, cost }: {
  nume: Cheie; ce: Cheie; n: number; tinta: number; m2: number; cost: number;
}) {
  const t = useT();
  const tn = useTn();
  const peste = n > tinta;
  return (
    <div className="rc-sect" style={{ display: "flex", alignItems: "baseline",
                                      gap: 10, flexWrap: "wrap" }}>
      <span>{t(nume)}</span>
      <span className={`rc-chip${peste ? " cmp-peste" : ""}`}
            title={t(peste ? "struct.cof.peste_tinta" : "struct.cof.in_tinta",
                     { tinta })}>
        {tn("struct.cof.nr_cofraje#n", n, { tinta })}
      </span>
      <span className="sg-muted" style={{ fontWeight: 400 }}>
        {t("struct.cof.m2_cost", { m2: nr(m2), cost: bani(cost) })}
      </span>
      <span className="sg-muted" style={{ fontWeight: 400, fontSize: 12 }}>
        {t(ce)}
      </span>
    </div>
  );
}

/** Membrii unui tip, ca linkuri spre pagina fiecărui element. Fără
 *  `laElement` (un apelant care n-are soluție), rămân etichete. */
export function MembriTip({ membri, laElement }: {
  membri: string[];
  laElement?: (ref: string) => string;
}) {
  return (
    <div className="rc-chips cmp-membri-lista">
      {membri.map((m) => laElement ? (
        <Link key={m} className="rc-chip cmp-element" to={laElement(m)}>{m}</Link>
      ) : (
        <span key={m} className="rc-chip">{m}</span>
      ))}
    </div>
  );
}

function TabelCofraje({ lista, laElement }: {
  lista: TipCofraj[];
  laElement?: (ref: string) => string;
}) {
  const t = useT();
  const tn = useTn();
  const [deschise, setDeschise] = useState<Set<string>>(() => new Set());
  if (!lista.length) {
    return <div className="sg-muted">{t("struct.cof.gol")}</div>;
  }
  const comuta = (tip: string) => setDeschise((d) => {
    const n = new Set(d);
    if (n.has(tip)) n.delete(tip); else n.add(tip);
    return n;
  });
  return (
    <table className="sg-table cmp-tabel">
      <thead>
        <tr>
          <th aria-hidden style={{ width: 22 }} />
          <th>{t("struct.cof.col_cofraj")}</th>
          <th>{t("struct.cof.col_ce")}</th>
          <th>{t("struct.cof.col_montari")}</th>
          <th>{t("struct.cof.col_suprafata")}</th>
          <th>{t("struct.comp.col_material")}</th>
          <th>{t("struct.comp.col_manopera")}</th>
          <th>{t("struct.comp.col_cost")}</th>
        </tr>
      </thead>
      <tbody>
        {lista.map((c) => {
          const deschis = deschise.has(c.tip);
          return (
            <Fragment key={c.tip}>
              <tr className={`cmp-rand-tip${deschis ? " deschis" : ""}`}
                  onClick={() => comuta(c.tip)}>
                <td>
                  <button type="button" className="cmp-chevron"
                          aria-expanded={deschis}
                          aria-label={t("struct.cof.membri_titlu")}
                          title={t("struct.cof.membri_titlu")}
                          onClick={(e) => { e.stopPropagation(); comuta(c.tip); }}>
                    {deschis ? "▾" : "▸"}
                  </button>
                </td>
                <td className="sg-mono">{c.tip}</td>
                <td>{c.descriere}</td>
                <td className="cmp-membri">{tn("struct.cof.montari#n", c.nr)}</td>
                <td className="sg-mono">{nr(c.cofraj_m2, 1)} m²</td>
                <td className="sg-mono">{bani(c.material_eur)}</td>
                <td className="sg-mono">{bani(c.manopera_eur)}</td>
                <td className="sg-mono"><strong>{bani(c.cost_eur)}</strong></td>
              </tr>
              {deschis && (
                <tr className="cmp-rand-membri">
                  <td />
                  <td colSpan={7}>
                    <MembriTip membri={c.membri} laElement={laElement} />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

/** Grila de deschideri pe care stă structura: traveele pe modulul de
 *  parcare și valorile distincte. */
export function GrilaDeschideri({ g }: { g: InteraxStalpi }) {
  const t = useT();
  const tn = useTn();
  if (!g.masurat) {
    return (
      <div className="sg-muted" data-testid="grila-nemasurata">
        {t("struct.grila.nemasurata")}
      </div>
    );
  }
  const afara = g.nr - g.pe_modul;
  return (
    <section className="cmp-familie" data-testid="grila-deschideri">
      <div className="rc-sect" style={{ display: "flex", alignItems: "baseline",
                                        gap: 10, flexWrap: "wrap" }}>
        <span>{t("struct.grila.titlu")}</span>
        <span className={`rc-chip${afara ? " cmp-peste" : ""}`}>
          {t("struct.grila.pe_modul", {
            peModul: g.pe_modul, nr: g.nr, modul: nr(g.modul_m, 2),
          })}
        </span>
        <span className="rc-chip">
          {tn("struct.grila.distincte#n", g.nr_distincte)}
        </span>
        <span className="sg-muted" style={{ fontWeight: 400, fontSize: 12 }}>
          {t("struct.grila.nota")}
        </span>
      </div>
      <table className="sg-table cmp-tabel">
        <thead>
          <tr>
            <th>{t("struct.grila.col_deschidere")}</th>
            <th>{t("struct.grila.col_travee")}</th>
            <th>{t("struct.grila.col_pe_modul")}</th>
            <th>{t("struct.grila.col_grinzi")}</th>
          </tr>
        </thead>
        <tbody>
          {g.distincte.map((d) => (
            <tr key={d.L_m}>
              <td className="sg-mono">{nr(d.L_m, 2)} m</td>
              <td className="sg-mono">{d.nr}</td>
              <td>{d.pe_modul
                ? <span className="fem-ok">
                    {t("struct.grila.multiplu", {
                      multiplu: d.locuri, modul: nr(g.modul_m, 2),
                    })}
                  </span>
                : <span className="st-bad">✗</span>}</td>
              <td className="sg-mono" style={{ fontSize: 12 }}>
                {d.grinzi.slice(0, 10).join(", ")}{d.grinzi.length > 10 ? "…" : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function Cofraje({ plan, laElement }: {
  plan: PlanCofrare;
  laElement?: (ref: string) => string;
}) {
  const t = useT();
  return (
    <div style={{ marginTop: 10 }} data-testid="plan-cofrare">
      <div className="sg-muted" style={{ marginBottom: 8, maxWidth: 760 }}>
        {t("struct.cof.intro", { total: plan.nr_tipuri, tinta: plan.tinta })}
      </div>
      {plan.interax && <GrilaDeschideri g={plan.interax} />}
      {FAMILII.map((f) => {
        const rez = plan.rezumat?.[f.cheie];
        const lista = plan[f.cheie] ?? [];
        return (
          <section key={f.cheie} className="cmp-familie">
            <Antet nume={f.nume} ce={f.ce} n={rez?.nr_tipuri ?? lista.length}
                   tinta={plan.tinta} m2={rez?.cofraj_m2 ?? 0}
                   cost={rez?.cost_eur ?? 0} />
            <TabelCofraje lista={lista} laElement={laElement} />
          </section>
        );
      })}
      <section className="cmp-familie" data-testid="fara-cofraj">
        <div className="rc-sect">{t("struct.cof.fara_cofraj")}</div>
        <ul className="sg-muted" style={{ marginTop: 4 }}>
          {plan.fara_cofraj.map((x) => (
            <li key={x.ce}><strong>{x.ce}</strong> — {x.de_ce}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
